import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Mail, Phone, Calendar, Users, ExternalLink, Search, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBookingsPage = () => {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/bookings`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const updateStatus = async (bookingId, status) => {
    const auth = localStorage.getItem('admin_auth');
    try {
      await axios.put(`${BACKEND_URL}/api/admin/bookings/${bookingId}/status?status=${status}`, {}, {
        headers: { Authorization: `Basic ${auth}` }
      });
      toast.success('Statut mis a jour!');
      fetchBookings();
    } catch (error) {
      toast.error('Erreur lors de la mise a jour');
    }
  };

  const handleExportCSV = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/bookings/export`, {
        headers: { Authorization: `Basic ${auth}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reservations-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV telecharge!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Erreur lors de l\'export CSV');
    }
  };

  // Client-side filtering by status
  const statusFiltered = filter === 'all'
    ? bookings
    : bookings.filter(b => b.status === filter);

  // Client-side search filtering by name, email, phone
  const filteredBookings = searchQuery.trim() === ''
    ? statusFiltered
    : statusFiltered.filter(b => {
        const q = searchQuery.toLowerCase();
        return (
          (b.name && b.name.toLowerCase().includes(q)) ||
          (b.email && b.email.toLowerCase().includes(q)) ||
          (b.phone && b.phone.toLowerCase().includes(q))
        );
      });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'confirmed': return 'bg-green-500/20 text-green-400';
      case 'cancelled': return 'bg-red-500/20 text-red-400';
      default: return 'bg-white/10 text-[#B9B2A8]';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-BE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div data-testid="admin-bookings-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-2xl text-[#F5F0E8] mb-1">Reservations VIP</h1>
          <p className="text-sm text-[#B9B2A8]">{bookings.length} reservation(s) au total</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B9B2A8]" />
            <input
              type="text"
              placeholder="Rechercher nom, email, tel..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-[#F5F0E8] placeholder:text-[#B9B2A8]/50 focus:outline-none focus:border-[#C9A84C]/50 w-full sm:w-64"
            />
          </div>
          {/* Export CSV */}
          <Button
            variant="outline"
            className="border-[#C9A84C]/30 text-[#C9A84C] hover:bg-[#C9A84C]/10 flex items-center gap-2"
            onClick={handleExportCSV}
          >
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
          {/* Status filter */}
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-full sm:w-40 bg-white/5 border-white/10 text-[#F5F0E8]">
              <SelectValue placeholder="Filtrer" />
            </SelectTrigger>
            <SelectContent className="bg-[#0B0B0C] border-white/10">
              <SelectItem value="all" className="text-[#F5F0E8]">Toutes</SelectItem>
              <SelectItem value="pending" className="text-yellow-400">En attente</SelectItem>
              <SelectItem value="confirmed" className="text-green-400">Confirmees</SelectItem>
              <SelectItem value="cancelled" className="text-red-400">Annulees</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Bookings List */}
      <div className="space-y-4">
        {filteredBookings.length === 0 ? (
          <div className="text-center py-12 text-[#B9B2A8]">
            {searchQuery.trim() !== '' ? 'Aucun resultat pour cette recherche' : 'Aucune reservation trouvee'}
          </div>
        ) : (
          filteredBookings.map((booking, index) => (
            <motion.div
              key={booking._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white/5 border border-white/10 rounded-xl p-5"
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-display text-lg text-[#F5F0E8]">{booking.name}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs uppercase ${getStatusColor(booking.status)}`}>
                      {booking.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                    <a href={`mailto:${booking.email}`} className="flex items-center gap-2 text-[#B9B2A8] hover:text-[#C9A84C]">
                      <Mail className="w-4 h-4" />
                      {booking.email}
                    </a>
                    <a href={`tel:${booking.phone}`} className="flex items-center gap-2 text-[#B9B2A8] hover:text-[#C9A84C]">
                      <Phone className="w-4 h-4" />
                      {booking.phone}
                    </a>
                    <div className="flex items-center gap-2 text-[#B9B2A8]">
                      <Calendar className="w-4 h-4" />
                      {booking.event}
                    </div>
                    <div className="flex items-center gap-2 text-[#B9B2A8]">
                      <Users className="w-4 h-4" />
                      {booking.guests} personnes
                    </div>
                  </div>
                  {booking.message && (
                    <p className="mt-3 text-sm text-[#B9B2A8] bg-white/5 rounded-lg p-3">
                      "{booking.message}"
                    </p>
                  )}
                  <p className="mt-2 text-xs text-[#B9B2A8]">
                    Recu le {formatDate(booking.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={booking.status}
                    onValueChange={(v) => updateStatus(booking._id, v)}
                  >
                    <SelectTrigger className="w-32 bg-white/5 border-white/10 text-[#F5F0E8]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0B0B0C] border-white/10">
                      <SelectItem value="pending" className="text-yellow-400">En attente</SelectItem>
                      <SelectItem value="confirmed" className="text-green-400">Confirmee</SelectItem>
                      <SelectItem value="cancelled" className="text-red-400">Annulee</SelectItem>
                    </SelectContent>
                  </Select>
                  <a
                    href={`https://wa.me/${booking.phone?.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Bonjour ${booking.name}, concernant votre reservation VIP pour ${booking.event}...`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" size="sm" className="border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10">
                      WhatsApp
                    </Button>
                  </a>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminBookingsPage;
